import { useTitle } from 'ahooks';
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  Row,
  Select,
  Space,
  Typography,
} from 'antd';
import { FC, useCallback, useMemo } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';

import CountrySelect from '../../../components/CountrySelect';
import EnhancedForm from '../../../components/EnhancedForm';
import { usePartnerConfig } from '../../../components/PartnerConfigProvider';
import { FormFieldType, FormResultInput } from '../../../graphql.generated';
import paths from '../../../paths.json';
import { useSearchQuery } from '../../../utils/location';

import { useFormQuery } from './Form.generated';
import { useSignupMutation } from './Signup.generated';
import styles from './styles.module.less';

const title = 'Sign Up';

const SignupForm: FC = () => {
  const history = useHistory();
  useTitle(title);
  const { termsAndConditions, privacyPolicy } = usePartnerConfig();
  const { searchQuery } = useSearchQuery<{
    ref?: string;
  }>();

  const referralId = searchQuery.ref;

  const { data, loading } = useFormQuery();
  const [signup, { loading: registering }] = useSignupMutation({
    onCompleted: () => {
      history.replace(paths.login as unknown as string, {
        success:
          'Thank you for the registration! We will get back to you soon!',
      });
    },
  });

  const form = useMemo(() => data?.forms?.[0], [data?.forms]);

  const fields = useMemo(() => form?.fields, [form?.fields]);

  const onFinish = useCallback(
    (values: FormResultInput) => {
      if (!form?.id) {
        return;
      }

      const results = Object.entries(values).map(([name, value]) => ({
        name,
        value: Array.isArray(value) ? value.join(', ') : value,
      }));

      void signup({
        variables: {
          input: {
            formId: form.id,
            referralId,
            results,
          },
        },
      });
    },
    [form?.id, referralId, signup],
  );

  if (!form && !loading) {
    return <Redirect to={paths.login} />;
  }

  return (
    <Row data-name="signup-page" gutter={[8, 28]}>
      <Col xs={24}>
        <Row align="middle" gutter={[8, 8]} justify="center">
          <Col xs={24}>
            <Typography.Title className={styles.title} level={3}>
              {title}
            </Typography.Title>
          </Col>
        </Row>
      </Col>
      <Col xs={24}>
        <EnhancedForm layout="vertical" loading={loading} onFinish={onFinish}>
          {fields?.map(
            (
              { allowedValues, defaultValue, mandatory: required, name, type },
              index,
            ) => {
              const key = `${name}-${index}`;
              if (type === FormFieldType.StaticText) {
                return (
                  <Typography.Paragraph key={key}>
                    {defaultValue}
                  </Typography.Paragraph>
                );
              }

              if (type === FormFieldType.Text) {
                return (
                  <Form.Item
                    key={key}
                    label={name}
                    name={name}
                    rules={[{ required }]}
                  >
                    <Input />
                  </Form.Item>
                );
              }

              if (type === FormFieldType.Email) {
                return (
                  <Form.Item
                    key={key}
                    label={name}
                    name={name}
                    rules={[{ required, type: 'email' }]}
                  >
                    <Input />
                  </Form.Item>
                );
              }

              if (type === FormFieldType.TextArea) {
                return (
                  <Form.Item
                    key={key}
                    label={name}
                    name={name}
                    rules={[{ required }]}
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                );
              }

              if (type === FormFieldType.Select) {
                return (
                  <Form.Item
                    key={key}
                    label={name}
                    name={name}
                    rules={[{ required }]}
                  >
                    <Select
                      allowClear
                      options={allowedValues?.map((value) => ({
                        label: value,
                        value,
                      }))}
                      showSearch
                    />
                  </Form.Item>
                );
              }

              if (type === FormFieldType.CountrySelect) {
                return (
                  <Form.Item
                    key={key}
                    label={name}
                    name={name}
                    rules={[{ required }]}
                  >
                    <CountrySelect
                      allowClear
                      countries={allowedValues || undefined}
                      showSearch
                    />
                  </Form.Item>
                );
              }

              if (type === FormFieldType.Checkbox) {
                return (
                  <Form.Item
                    key={key}
                    label={name}
                    name={name}
                    rules={[{ required }]}
                  >
                    <Checkbox.Group>
                      <Space direction="vertical">
                        {allowedValues?.map((value) => {
                          return (
                            <Checkbox key={value} value={value}>
                              {value}
                            </Checkbox>
                          );
                        })}
                      </Space>
                    </Checkbox.Group>
                  </Form.Item>
                );
              }

              return null;
            },
          )}
          {(termsAndConditions || privacyPolicy) && (
            <Form.Item
              name="Terms and Conditions"
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (value) {
                      return Promise.resolve();
                    }

                    return Promise.reject('Required');
                  },
                },
              ]}
              valuePropName="checked"
            >
              <Checkbox>
                I agree to the{' '}
                {termsAndConditions && (
                  <Link to={paths.termsAndConditions}>
                    Terms and Conditions
                  </Link>
                )}
                {termsAndConditions && privacyPolicy && <> and </>}
                {privacyPolicy && (
                  <Link to={paths.privacyPolicy}>Privacy Policy</Link>
                )}
              </Checkbox>
            </Form.Item>
          )}
          <Row align="middle" gutter={[8, 16]} justify="center">
            <Col xs={24}>
              <Button
                block
                htmlType="submit"
                loading={registering}
                size="large"
                type="primary"
              >
                Send Application
              </Button>
            </Col>
            <Col>
              <Link to={paths.login}>
                <Button type="link">Already have an account?</Button>
              </Link>
            </Col>
          </Row>
        </EnhancedForm>
      </Col>
    </Row>
  );
};

SignupForm.displayName = 'SignupForm';

export default SignupForm;
